import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { createCompatInvite } from '@/api/client';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * "Share with a friend" CTA shown after a compatibility result.
 *
 * Flow:
 *   1. Tap → POST /v1/compat/invite → backend returns `share_link`
 *      (a t.me/<bot>/<short>?startapp=compat_<UUID> URL).
 *   2. We hand the link to Telegram's `openTelegramLink` with the share/url
 *      endpoint, which pops the native "Forward to chat..." picker.
 *      The friend taps a chat → Telegram drops a nicely-previewed message
 *      with our deep link.
 *   3. If Telegram WebApp isn't available (dev/browser), we fall back to
 *      the Web Share API, then to a clipboard copy with toast.
 *
 * No own modal needed — Telegram's picker handles the rest.
 */
export default function ShareButton({
  shareMessage,
  shareLink,
  label,
  className = '',
}: {
  shareMessage: string;
  /**
   * If provided, used directly as the share URL.
   * If omitted, we fall back to creating a one-off compat invite
   * (the historical default for the Compat screen).
   */
  shareLink?: string;
  /** Override the visible button label. */
  label?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const { tg, haptic } = useTelegram();
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const flashToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const share = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const share_link = shareLink ?? (await createCompatInvite()).share_link;
      haptic?.notificationOccurred('success');

      // Preferred: Telegram's in-app share picker.
      if (tg?.openTelegramLink) {
        const url =
          'https://t.me/share/url'
          + `?url=${encodeURIComponent(share_link)}`
          + `&text=${encodeURIComponent(shareMessage)}`;
        tg.openTelegramLink(url);
        return;
      }

      // Browser fallback 1: Web Share API (mobile browsers).
      const navAny = navigator as Navigator & {
        share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
      };
      if (navAny.share) {
        await navAny.share({ text: shareMessage, url: share_link });
        return;
      }

      // Browser fallback 2: clipboard copy + toast.
      try {
        await navigator.clipboard.writeText(`${shareMessage}\n${share_link}`);
        flashToast(t('compatibility.share_copied'));
      } catch {
        flashToast(share_link);
      }
    } catch (e) {
      setError((e as Error).message);
      haptic?.notificationOccurred('error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={share}
        disabled={busy}
        className="btn-primary w-full disabled:opacity-40
                   flex items-center justify-center gap-2"
      >
        <span>✦</span>
        <span>
          {busy
            ? t('compatibility.share_preparing')
            : (label ?? t('compatibility.share_link'))}
        </span>
      </button>

      {error && (
        <p className="mt-2 text-center text-xs text-rose-400">{error}</p>
      )}

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap
                       rounded-full border border-gold-500/50 bg-night-800/90 px-3 py-1.5
                       text-xs text-gold-200 shadow-lg"
          >
            ✓ {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
